import {
  Button,
  Eye,
  Input,
  Pencil,
  Plus,
  RefreshCw,
  Stethoscope,
  Trash2,
  X,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { Pagination } from "../../components/molecules/Pagination";
import { specialtyApi } from "../../services/specialty.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  createSpecialtyRequest,
  deleteSpecialtyRequest,
  fetchSpecialtiesRequest,
  selectSpecialties,
  selectSpecialtiesDeleting,
  selectSpecialtiesLoading,
  selectSpecialtiesLoadedLang,
  selectSpecialtiesSaving,
  updateSpecialtyRequest,
} from "../../store/reducers/specialtySlice";
import {
  SpecialtyDto,
  SpecialtyLabelVM,
  SpecialtyPayloadVM,
} from "../../types/specialty.definition";
import { textDirection } from "../../utils/text-direction-utils";

type LangKey = "en" | "fr" | "ar";

const LANGS: { key: LangKey; label: string }[] = [
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
  { key: "ar", label: "العربية" },
];

function emptyLabel(): SpecialtyLabelVM {
  return { en: "", fr: "", ar: "" };
}

// ─── View Modal ────────────────────────────────────────────────────────────────

interface ViewModalProps {
  specialty: SpecialtyDto | null;
  onClose: () => void;
}

const ViewModal: React.FC<ViewModalProps> = ({ specialty, onClose }) => {
  const { t } = useTranslation();
  if (!specialty) return null;
  return (
    <Modal
      isOpen={!!specialty}
      onClose={onClose}
      size="sm"
      title={t("admin.specialties.viewTitle")}
    >
      <div className="p-6 space-y-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
            {t("admin.specialties.table.id")}
          </p>
          <p className="text-sm font-mono text-neutral-900">#{specialty.id}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
            {t("admin.specialties.table.code")}
          </p>
          <p className="text-sm font-semibold text-neutral-900">
            {specialty.code}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-1">
            {t("admin.specialties.table.label")}
          </p>
          <p className="text-sm text-neutral-900">
            {specialty.label || (
              <span className="italic text-neutral-400">
                {t("admin.specialties.noLabel")}
              </span>
            )}
          </p>
        </div>
        <div className="flex justify-end pt-2 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const SpecialtyManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const specialties = useAppSelector(selectSpecialties);
  const loadedLang = useAppSelector(selectSpecialtiesLoadedLang);
  const isLoading = useAppSelector(selectSpecialtiesLoading);
  const isSaving = useAppSelector(selectSpecialtiesSaving);
  const isDeleting = useAppSelector(selectSpecialtiesDeleting);

  const [lang, setLang] = useState<LangKey>("fr");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState<SpecialtyDto | null>(
    null
  );
  const [viewingSpecialty, setViewingSpecialty] = useState<SpecialtyDto | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Form state
  const [activeLang, setActiveLang] = useState<LangKey>("fr");
  const [code, setCode] = useState("");
  const [label, setLabel] = useState<SpecialtyLabelVM>(emptyLabel());
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const editingIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (specialties.length === 0 || loadedLang !== lang) {
      dispatch(fetchSpecialtiesRequest({ lang }));
    }
    return () => {
      dispatch(clearError());
    };
  }, [dispatch, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => dispatch(fetchSpecialtiesRequest({ lang }));

  const filtered = specialties.filter(
    s =>
      s.code.toLowerCase().includes(search.toLowerCase()) ||
      (s.label ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedSpecialties = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  // ─── Modal helpers ──────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingSpecialty(null);
    setCode("");
    setLabel(emptyLabel());
    setActiveLang("fr");
    setIsFormOpen(true);
  };

  const openEditModal = (specialty: SpecialtyDto) => {
    editingIdRef.current = specialty.id;
    setEditingSpecialty(specialty);
    setCode(specialty.code);
    // Pre-fill current label as fallback while translations load
    setLabel({
      en: specialty.label ?? "",
      fr: specialty.label ?? "",
      ar: specialty.label ?? "",
    });
    setActiveLang("fr");
    setIsFormOpen(true);

    // Fetch each language translation in parallel.
    // Guard against stale responses if the user switches specialties quickly.
    const requestedId = specialty.id;
    setIsLoadingTranslations(true);
    specialtyApi
      .getAllTranslations(requestedId)
      .then(translations => {
        if (editingIdRef.current === requestedId) {
          setLabel(translations);
        }
      })
      .catch(() => {
        /* keep fallback already set above */
      })
      .finally(() => {
        if (editingIdRef.current === requestedId) {
          setIsLoadingTranslations(false);
        }
      });
  };

  const closeFormModal = () => {
    editingIdRef.current = null;
    setIsFormOpen(false);
    setEditingSpecialty(null);
  };

  // ─── Save ───────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!code.trim()) return;

    if (editingSpecialty) {
      dispatch(
        updateSpecialtyRequest({
          id: editingSpecialty.id,
          data: { id: editingSpecialty.id, code: code.trim(), label },
          onSuccess: () => {
            closeFormModal();
            dispatch(fetchSpecialtiesRequest({ lang }));
            toast.success(t("admin.specialties.toast.updateSuccess"));
          },
          onError: () => {
            toast.error(t("admin.specialties.toast.updateError"));
          },
        })
      );
    } else {
      const payload: SpecialtyPayloadVM = {
        code: code.trim(),
        label,
      };
      dispatch(
        createSpecialtyRequest({
          data: payload,
          onSuccess: () => {
            closeFormModal();
            dispatch(fetchSpecialtiesRequest({ lang }));
            toast.success(t("admin.specialties.toast.createSuccess"));
          },
          onError: () => {
            toast.error(t("admin.specialties.toast.createError"));
          },
        })
      );
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = () => {
    if (deletingId === null) return;
    const id = deletingId;
    setDeletingId(null);
    dispatch(
      deleteSpecialtyRequest({
        id,
        onSuccess: () => {
          dispatch(fetchSpecialtiesRequest({ lang }));
          toast.success(t("admin.specialties.toast.deleteSuccess"));
        },
        onError: () => {
          toast.error(t("admin.specialties.toast.deleteError"));
        },
      })
    );
  };

  return (
    <div className="page-container">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.specialties.title")}
          </h2>
          <p className="text-neutral-600">{t("admin.specialties.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Language selector */}
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
            {LANGS.map(({ key, label: langLabel }) => (
              <button
                key={key}
                onClick={() => {
                  setLang(key);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  lang === key
                    ? "bg-ews-primary text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {langLabel}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            onClick={openCreateModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.specialties.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* ─── Search ──────────────────────────────────────────────────────── */}
      <div className="page-section pb-0">
        <div className="relative max-w-sm">
          <Input
            size="sm"
            placeholder={t("admin.specialties.searchPlaceholder")}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ─── Table ───────────────────────────────────────────────────────── */}
      <div className="page-section">
        {isLoading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Stethoscope className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {search
                ? t("admin.specialties.noResults")
                : t("admin.specialties.noSpecialties")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {search
                ? t("admin.specialties.noResultsDescription")
                : t("admin.specialties.noSpecialtiesDescription")}
            </p>
            {!search && (
              <div className="mt-6">
                <Button onClick={openCreateModal}>
                  <Plus className="mr-2 w-4 h-4" />
                  {t("admin.specialties.addNew")}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-neutral-200">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-16">
                      {t("admin.specialties.table.id")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-56">
                      {t("admin.specialties.table.code")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600">
                      {t("admin.specialties.table.label")}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-neutral-600 w-32">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {paginatedSpecialties.map(specialty => (
                    <tr
                      key={specialty.id}
                      className="hover:bg-neutral-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-neutral-500 font-mono text-xs">
                        #{specialty.id}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                          {specialty.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">
                        {specialty.label || (
                          <span className="italic text-neutral-400">
                            {t("admin.specialties.noLabel")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center space-x-1">
                          <button
                            onClick={() => setViewingSpecialty(specialty)}
                            className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                            title={t("common.view")}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(specialty)}
                            className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                            title={t("common.edit")}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingId(specialty.id)}
                            className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                            title={t("common.delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs text-neutral-500 border-t border-neutral-100 bg-neutral-50">
                {t("admin.specialties.count", { count: totalItems })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showPageNumbers={true}
                  maxVisiblePages={5}
                  showItemsPerPageSelector={true}
                  itemsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Create / Edit Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={isFormOpen}
        onClose={closeFormModal}
        size="md"
        title={
          editingSpecialty
            ? t("admin.specialties.editTitle")
            : t("admin.specialties.addNew")
        }
      >
        <div className="p-6 space-y-5">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t("admin.specialties.form.code")}
            </label>
            <Input
              size="sm"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder={t("admin.specialties.form.codePlaceholder")}
            />
            <p className="mt-1 text-xs text-neutral-400">
              {t("admin.specialties.form.codeHint")}
            </p>
          </div>

          {/* Language tabs for label */}
          <div>
            <div className="flex items-center border-b border-neutral-200 mb-4">
              {LANGS.map(({ key, label: langLabel }) => (
                <button
                  key={key}
                  onClick={() => setActiveLang(key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeLang === key
                      ? "border-ews-primary text-ews-primary"
                      : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {langLabel}
                </button>
              ))}
              {isLoadingTranslations && (
                <span className="ml-auto flex items-center gap-1 text-xs text-neutral-400 pb-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  {t("common.loading")}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.specialties.form.label")}
              </label>
              <Input
                size="sm"
                dir={textDirection(activeLang)}
                value={label[activeLang]}
                onChange={e =>
                  setLabel(prev => ({ ...prev, [activeLang]: e.target.value }))
                }
                placeholder={t("admin.specialties.form.labelPlaceholder")}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={closeFormModal}
              disabled={isSaving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving || !code.trim()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── View Modal ──────────────────────────────────────────────────── */}
      <ViewModal
        specialty={viewingSpecialty}
        onClose={() => setViewingSpecialty(null)}
      />

      {/* ─── Delete Confirmation ──────────────────────────────────────────── */}
      <ConfirmationModal
        isOpen={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDeleteConfirm}
        title={t("admin.specialties.deleteConfirm.title")}
        message={t("admin.specialties.deleteConfirm.message")}
        variant="danger"
        isLoading={isDeleting}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
